import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatusPill } from "@/components/shared/StatusPill";
import { Loading } from "@/components/shared/Loading";
import { EmptyState } from "@/components/shared/EmptyState";
import { useFinanceGateQueue } from "@/api/hooks";
import { money } from "@/lib/format";
import { ShieldCheck, ChevronRight } from "lucide-react";
import { BusinessStage, financeReviewStage } from "@/components/shared/BusinessStage";

// Phase 4.5 — this is the real Finance "Order Review" workspace: every
// VERIFIED accepted order that hasn't yet become a Job, with its current
// Finance Gate state, fed live by GET /finance-gate/queue (no seed/static
// rows). Kept at the pre-existing "Credit Gates" nav slot rather than
// adding a new nav item.
export default function FinanceCredit() {
  const navigate = useNavigate();
  const { data: queue, isLoading } = useFinanceGateQueue();
  if (isLoading || !queue) return <Loading />;

  return (
    <>
      <PageHeader title="Order Review" subtitle="Accepted customer orders awaiting a Finance decision before Operations can execute" />
      <div className="flex-1 overflow-y-auto p-6 space-y-3">
        {queue.length === 0 && <EmptyState icon={ShieldCheck} title="Nothing awaiting Finance review" sub="Every accepted order has either been released to a Job or has no open Finance action." />}
        {queue.map((row) => (
          <button key={row.acceptanceId} data-testid={`fg-queue-${row.acceptanceId}`}
            onClick={() => navigate(`/finance/orders/${row.acceptanceId}`)}
            className="w-full text-left rounded-xl border border-slate-200 bg-white p-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-display font-semibold text-slate-800">{row.customer}</span>
                <StatusPill status={row.financeGateStatus || "Pending"} />
              </div>
              <div className="text-[12px] text-slate-500 mt-0.5">{row.quotationId} V{row.quotationVersion} · {money(row.acceptedAmount, row.currency)}</div>
              {row.reason && <div className="text-[11px] text-slate-400 mt-0.5 truncate max-w-xl">{row.reason}</div>}
              <div className="mt-2"><BusinessStage stage={financeReviewStage(row.financeGateStatus || "Pending Finance review")} /></div>
            </div>
            <ChevronRight className="h-5 w-5 text-slate-400 shrink-0" />
          </button>
        ))}
      </div>
    </>
  );
}
